import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Send, Loader2, Bell, X, FileText, CheckCircle2, Eye, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS = {
  sent: { label: "Sent", cls: "bg-slate-100 text-slate-700", Icon: Mail },
  opened: { label: "Opened", cls: "bg-blue-50 text-blue-700", Icon: Eye },
  completed: { label: "Completed", cls: "bg-emerald-50 text-emerald-700", Icon: CheckCircle2 },
};

function SendFormDialog({ employeeId, open, onClose, onSent }) {
  const [catalog, setCatalog] = useState([]);
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState(new Set());
  const [note, setNote] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPicked(new Set()); setNote(""); setDueDate(""); setQuery("");
    axios.get(`${API}/form-requests/catalog`).then((r) => setCatalog(r.data.items)).catch(() => toast.error("Could not load forms"));
  }, [open]);

  const key = (i) => `${i.source}:${i.id}`;
  const toggle = (i) => setPicked((p) => { const n = new Set(p); n.has(key(i)) ? n.delete(key(i)) : n.add(key(i)); return n; });
  const shown = catalog.filter((i) => (i.title || "").toLowerCase().includes(query.toLowerCase()));

  const send = async () => {
    setBusy(true);
    try {
      const items = catalog.filter((i) => picked.has(key(i))).map((i) => ({ source: i.source, id: i.id }));
      const r = await axios.post(`${API}/employees/${employeeId}/form-requests`, { items, note: note || null, due_date: dueDate || null, notify });
      const n = r.data.created.length;
      const emailed = r.data.created.filter((x) => x.notified).length;
      toast.success(`${n} form${n > 1 ? "s" : ""} sent${notify ? (emailed ? " — employee emailed" : " (no email on file, visible in their portal)") : ""}`);
      onSent(); onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="send-form-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Send className="w-4 h-4 text-teal-600" /> Send a form to fill out</DialogTitle></DialogHeader>
        <Input placeholder="Search orientation documents & templates…" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="send-form-search" />
        <div className="max-h-64 overflow-y-auto divide-y border rounded-md">
          {shown.length === 0 && <p className="p-4 text-sm text-slate-500 text-center">No forms found</p>}
          {shown.map((i) => (
            <label key={key(i)} className="flex items-center gap-3 p-2.5 text-sm hover:bg-slate-50 cursor-pointer" data-testid={`send-form-item-${i.source}-${i.id}`}>
              <Checkbox checked={picked.has(key(i))} onCheckedChange={() => toggle(i)} />
              <FileText className="w-4 h-4 text-slate-400 shrink-0" />
              <span className="flex-1 truncate">{i.title}</span>
              <Badge variant="outline" className="text-[10px]">{i.source === "kit" ? "Orientation" : "Template"}</Badge>
              <Badge variant="outline" className={`text-[10px] ${i.fillable ? "text-teal-700 border-teal-200" : "text-slate-500"}`}>{i.fillable ? `${i.field_count} fields` : "Sign only"}</Badge>
            </label>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Note to employee (optional)</Label><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Please complete before Friday" data-testid="send-form-note" /></div>
          <div><Label className="text-xs">Due date (optional)</Label><Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="send-form-due" /></div>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700"><Checkbox checked={notify} onCheckedChange={(v) => setNotify(!!v)} data-testid="send-form-notify" /> Email the employee a link now</label>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={send} disabled={busy || picked.size === 0} className="gap-1" data-testid="send-form-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send {picked.size || ""} form{picked.size === 1 ? "" : "s"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function FormRequestsPanel({ employeeId, onChanged }) {
  const [requests, setRequests] = useState([]);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/employees/${employeeId}/form-requests`).then((r) => setRequests(r.data.requests)).catch(() => {});
  }, [employeeId]);
  useEffect(() => { load(); }, [load]);

  const remind = async (r) => {
    try { const x = await axios.post(`${API}/form-requests/${r.id}/remind`); toast.success(x.data.email_sent ? "Reminder emailed" : "Reminder recorded (no email on file)"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not send reminder"); }
  };
  const cancel = async (r) => {
    if (!window.confirm(`Cancel "${r.title}" for this employee?`)) return;
    try { await axios.delete(`${API}/form-requests/${r.id}`); toast.success("Request cancelled"); load(); onChanged?.(); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not cancel"); }
  };
  const days = (iso) => Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid="form-requests-panel">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-900 text-sm flex items-center gap-2"><Send className="w-4 h-4 text-teal-600" /> Forms sent to this employee</h3>
        <Button size="sm" onClick={() => setOpen(true)} className="gap-1 bg-teal-600 hover:bg-teal-700" data-testid="send-form-btn"><Send className="w-3.5 h-3.5" /> Send a form</Button>
      </div>
      {requests.length === 0 ? (
        <p className="text-xs text-slate-500">Nothing sent yet — pick any orientation document or form template and send it to fill out and sign.</p>
      ) : (
        <div className="divide-y">
          {requests.map((r) => {
            const s = STATUS[r.status] || STATUS.sent;
            const overdue = r.status !== "completed" && ((r.due_date && r.due_date < new Date().toISOString().slice(0, 10)) || days(r.sent_at) >= 3);
            return (
              <div key={r.id} className="flex flex-wrap items-center gap-2 py-2 text-sm" data-testid={`form-request-${r.id}`}>
                <FileText className="w-4 h-4 text-slate-400" />
                <span className="flex-1 min-w-[160px] truncate">{r.title}</span>
                <Badge className={`${s.cls} gap-1 text-[11px]`} data-testid={`form-request-status-${r.id}`}><s.Icon className="w-3 h-3" /> {s.label}</Badge>
                <span className="text-[11px] text-slate-500">
                  sent {new Date(r.sent_at).toLocaleDateString()}{r.due_date ? ` · due ${r.due_date}` : ""}{r.reminder_count ? ` · ${r.reminder_count} reminder${r.reminder_count > 1 ? "s" : ""}` : ""}
                  {overdue && <span className="text-amber-700"> · {days(r.sent_at)} days waiting</span>}
                </span>
                {r.status !== "completed" && (
                  <>
                    <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs" onClick={() => remind(r)} data-testid={`form-request-remind-${r.id}`}><Bell className="w-3 h-3" /> Remind</Button>
                    <Button size="sm" variant="ghost" className="h-7 text-red-600" onClick={() => cancel(r)} data-testid={`form-request-cancel-${r.id}`}><X className="w-3 h-3" /></Button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
      <SendFormDialog employeeId={employeeId} open={open} onClose={() => setOpen(false)} onSent={() => { load(); onChanged?.(); }} />
    </div>
  );
}
